import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  MdOutlineDashboard,
  MdPeople,
  MdCalendarMonth,
  MdAccessTime,
  MdCampaign,
  MdCategory,
  MdWork,
  MdEventNote,
  MdChevronLeft,
  MdChevronRight,
  MdLogout,
  MdVerified,
  MdStorefront,
} from "react-icons/md";

import { BiSolidCoupon } from "react-icons/bi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import logo from "../assets/OG Logo.png";
import { useAuth } from "../context/AuthProvider";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen, collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, logoutSession } = useAuth();

  const [activeButton, setActiveButton] = useState(
    localStorage.getItem("activeButton") || "Dashboard"
  );

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsSidebarOpen]);

  const handleLogout = () => {
    if (logoutSession) {
      logoutSession();
    } else {
      localStorage.clear();
    }
    navigate("/login");
  };

  const adminMenuGroups = [
    {
      groupTitle: "MAIN",
      items: [{ name: "Dashboard", href: "/", icon: <MdOutlineDashboard /> }],
    },
    {
      groupTitle: "OPERATIONS",
      items: [
        { name: "Appointments", href: "/apoinment", icon: <MdEventNote /> },
        { name: "Orders", href: "/orders", icon: <MdCalendarMonth /> },
        { name: "Corporate Enquiry", href: "/Co-enquiry", icon: <HiOutlineOfficeBuilding /> },
      ],
    },
    {
      groupTitle: "CATALOG",
      items: [
        { name: "Services", href: "/services", icon: <MdWork /> },
        { name: "Categories", href: "/categorys", icon: <MdCategory /> },
        { name: "Timeslots", href: "/timeslots", icon: <MdAccessTime /> },
        { name: "Banners", href: "/banner", icon: <MdCampaign /> },
        { name: "Most Booked", href: "/most-booked", icon: <BiSolidCoupon /> },
      ],
    },
    {
      groupTitle: "MANAGEMENT",
      items: [
        { name: "Users", href: "/users", icon: <MdPeople /> },
        { name: "Employees", href: "/emp", icon: <MdPeople /> },
        { name: "Vendors", href: "/vendors", icon: <MdStorefront /> },
      ],
    },
  ];

  const vendorMenuGroups = [
    {
      groupTitle: "MAIN",
      items: [{ name: "Dashboard", href: "/", icon: <MdOutlineDashboard /> }],
    },
    {
      groupTitle: "PRODUCTS & CATALOG",
      items: [
        { name: "Services & Products", href: "/services", icon: <MdWork /> },
        { name: "Categories", href: "/categorys", icon: <MdCategory /> },
      ],
    },
    {
      groupTitle: "OPERATIONS",
      items: [
        { name: "Orders", href: "/orders", icon: <MdCalendarMonth /> },
      ],
    },
  ];

  const employeeMenuGroups = [
    {
      groupTitle: "MAIN",
      items: [{ name: "Dashboard", href: "/", icon: <MdOutlineDashboard /> }],
    },
    {
      groupTitle: "OPERATIONS",
      items: [
        { name: "Appointments", href: "/apoinment", icon: <MdEventNote /> },
        { name: "Orders", href: "/orders", icon: <MdCalendarMonth /> },
        { name: "Corporate Enquiry", href: "/Co-enquiry", icon: <HiOutlineOfficeBuilding /> },
      ],
    },
  ];

  const menuGroups =
    role === "vendor"
      ? vendorMenuGroups
      : role === "employee"
      ? employeeMenuGroups
      : adminMenuGroups;

  useEffect(() => {
    const currentPath = location.pathname;
    let foundName = null;

    menuGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.href === currentPath) foundName = item.name;
      });
    });

    if (foundName) {
      setActiveButton(foundName);
      localStorage.setItem("activeButton", foundName);
    }
  }, [location.pathname, menuGroups]);

  if (!role) return null;

  const isCollapsed = collapsed && window.innerWidth >= 1024;

  return (
    <>
      <aside
        className={`fixed top-0 left-0 z-50 h-screen flex flex-col justify-between
        bg-[#0B0A3D] bg-[radial-gradient(140%_70%_at_0%_0%,#1F1C66_0%,#0B0A3D_60%)]
        transition-[transform,width] duration-300 motion-reduce:transition-none
        border-r border-white/10
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        ${isCollapsed ? "w-20" : "w-64"}
        [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-white/10`}
      >
        {/* Top Header & Brand */}
        <div>
          <div className="relative px-4 pt-6 pb-5 border-b border-white/10 overflow-hidden">
            {/* Glowing Brass Lamp Aura */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-10 -left-10 h-36 w-36 rounded-full bg-[#C9A227]/20 blur-3xl"
            />
            <Link
              to="/"
              className={`relative flex items-center gap-3.5 rounded-xl transition-opacity hover:opacity-90 ${
                isCollapsed ? "justify-center px-0" : "px-2"
              }`}
            >
              <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 shadow-lg shrink-0">
                <img src={logo} alt="OurGenie" className="h-7 w-7 object-contain" />
              </div>

              {!isCollapsed && (
                <div className="min-w-0">
                  <p
                    className="text-[1.1rem] font-bold tracking-tight text-[#F7F4ED]"
                    style={{ fontFamily: '"Fraunces", serif' }}
                  >
                    OurGenie
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="h-2 w-2 rounded-full bg-[#2F9E68] animate-pulse" />
                    <span className="inline-flex items-center rounded-md bg-[#C9A227]/20 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-[#E4C766]">
                      {role}
                    </span>
                  </div>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className="px-3 py-4 overflow-y-auto max-h-[calc(100vh-190px)] space-y-5">
            {menuGroups.map((group) => (
              <div key={group.groupTitle}>
                {!isCollapsed && (
                  <p className="px-3 mb-2 text-[0.68rem] font-bold uppercase tracking-wider text-[#8886A8]">
                    {group.groupTitle}
                  </p>
                )}
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const isActive = activeButton === item.name || location.pathname === item.href;

                    return (
                      <li key={item.name}>
                        <button
                          title={isCollapsed ? item.name : undefined}
                          className={`group relative flex items-center w-full rounded-xl py-2.5 text-[0.88rem] font-medium transition-all duration-200
                          ${isCollapsed ? "justify-center px-0" : "justify-start px-3.5 gap-3.5"}
                          ${
                            isActive
                              ? "bg-gradient-to-r from-white/[0.12] to-white/[0.04] text-[#F7F4ED] shadow-sm border border-white/10"
                              : "text-[#C7C6D9] hover:bg-white/5 hover:text-[#F7F4ED]"
                          }`}
                          onClick={() => {
                            setActiveButton(item.name);
                            localStorage.setItem("activeButton", item.name);
                            if (window.innerWidth < 1024) setIsSidebarOpen(false);
                            navigate(item.href);
                          }}
                        >
                          {/* Active Golden Left Glow Bar */}
                          <span
                            className={`absolute left-0 top-1/2 h-6 -translate-y-1/2 w-[3.5px] rounded-r-full bg-[#C9A227] transition-all duration-200 ${
                              isActive
                                ? "opacity-100 shadow-[0_0_12px_2px_rgba(201,162,39,0.7)]"
                                : "opacity-0"
                            }`}
                          />

                          <span
                            className={`text-xl transition-colors duration-200 ${
                              isActive ? "text-[#E4C766]" : "text-[#8886A8] group-hover:text-[#E4C766]"
                            }`}
                          >
                            {item.icon}
                          </span>

                          {!isCollapsed && (
                            <span className="truncate tracking-wide">{item.name}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* User Profile & Footer Actions */}
        <div className="p-3 border-t border-white/10 bg-white/[0.02]">
          {!isCollapsed ? (
            <div className="flex items-center justify-between p-2 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#C9A227]/20 text-[#E4C766] font-bold text-sm shrink-0 border border-[#C9A227]/30">
                  {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#F7F4ED] truncate">{user?.name || "User"}</p>
                  <p className="text-[0.68rem] text-[#8886A8] truncate">{user?.email || "Session Active"}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                title="Logout"
                className="p-1.5 text-[#C7C6D9] hover:text-[#D6455A] hover:bg-[#D6455A]/15 rounded-lg transition-colors"
              >
                <MdLogout size={18} />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              title="Logout"
              className="flex items-center justify-center w-full py-2.5 rounded-xl text-[#C7C6D9] hover:text-[#D6455A] hover:bg-[#D6455A]/15 transition-colors"
            >
              <MdLogout size={20} />
            </button>
          )}

          {/* Desktop Collapse Toggle
          <div className="hidden lg:flex items-center justify-end mt-2 pt-2 border-t border-white/5">
            <button
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-[#C7C6D9] border border-white/10 transition-colors hover:bg-white/10 hover:text-[#F7F4ED]"
            >
              {collapsed ? <MdChevronRight size={18} /> : <MdChevronLeft size={18} />}
            </button>
          </div> */}
        </div>
      </aside>

      {/* Mobile Backdrop Overlay */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-[#0B0A3D]/70 backdrop-blur-sm lg:hidden transition-opacity"
        />
      )}
    </>
  );
};

export default Sidebar;